import { useEffect, useState } from 'react';

import { BASE_PATH } from '@ai-studio/shared';

import { api } from '../../lib/api.js';
import { DownloadButton, useInstaller } from './DownloadPage.js';
import { GameCard, type LibraryGame } from './LibraryPages.js';
import { Link, signInUrl, type SiteUser } from './WebsiteApp.js';

/** The beam a prism splits: the site's signature colours, red to violet. */
const SPECTRUM = ['#ff5d6c', '#ff9f43', '#ffd84d', '#4ce08a', '#3fd6e8', '#5b8cff', '#a77bff'];

const FEATURES: { tag: string; title: string; text: string; color: string }[] = [
  { tag: 'SCENE', title: 'Build the world', text: 'A real-time 3D and 2D scene editor: drag, snap, paint terrain, scatter forests, light it, and press Play.', color: SPECTRUM[0]! },
  { tag: 'SCRIPT', title: 'Script anything', text: 'Small, readable scripts on any entity, hot-reloaded while the game runs. No boilerplate, no build step.', color: SPECTRUM[1]! },
  { tag: 'PRISMA', title: 'Make it with Prisma', text: 'Your AI game-dev companion builds with you: worlds, scripts, characters, outfits, animations and sound, all with the engine’s own tools.', color: SPECTRUM[2]! },
  { tag: 'ART', title: 'Model, paint and sculpt', text: 'Block out meshes, paint textures right on the model, draw curves and pixel art, without leaving the editor.', color: SPECTRUM[3]! },
  { tag: 'AUDIO', title: 'Sound that fits', text: 'Design sound effects and music from presets and synth graphs, then drop them straight into the scene.', color: SPECTRUM[4]! },
  { tag: 'ONLINE', title: 'Multiplayer your way', text: 'Read out a join code and up to 8 friends are in: the Prism Engine Cloud relay handles the networking. Or run your own dedicated server for bigger worlds.', color: SPECTRUM[5]! },
  { tag: 'SHIP', title: 'Export everywhere', text: 'One button: Windows (DirectX 12 or Vulkan), the web, a dedicated server, or straight into the Prism Engine Cloud game library.', color: SPECTRUM[6]! },
];

const STEPS: { n: string; title: string; text: string }[] = [
  { n: '01', title: 'Sketch it', text: 'Start from a template or an empty world. Tell Prisma what you are picturing.' },
  { n: '02', title: 'Build it', text: 'Shape the level, write the rules, make the art and the sound. Play-test every change instantly.' },
  { n: '03', title: 'Share it', text: 'Publish to the library in one click. Anyone can play in their browser; friends join with a code.' },
];

/**
 * The website's front page: what Prism is, a look at the editor, the newest
 * games, and the ways in (sign in for the browser editor, or the desktop app).
 */
export function HomePage({ user }: { user: SiteUser | null }) {
  const [games, setGames] = useState<LibraryGame[] | null>(null);
  const installer = useInstaller();

  useEffect(() => {
    api
      .get<{ games: LibraryGame[] }>('/library/games?sort=popular')
      .then((r) => setGames(r.games.slice(0, 6)))
      .catch(() => setGames([]));
  }, []);

  const start = user ? (
    <Link to="/projects" className="btn btn-primary btn-lg">
      Open my projects
    </Link>
  ) : (
    <a className="btn btn-primary btn-lg" href={signInUrl(`${BASE_PATH}/projects`)}>
      Start building, free
    </a>
  );

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-grid" aria-hidden="true" />
        <div className="hero-inner">
          <div className="hero-copy">
            <p className="eyebrow">
              <span className="eyebrow-dot" /> The Prism Engine Cloud game engine{installer?.available ? ` · v${installer.version}` : ''}
            </p>
            <h1 className="hero-title">
              One idea in.
              <br />
              <span className="spectrum-text">A whole game out.</span>
            </h1>
            <p className="hero-sub">
              Prism Engine Cloud is a game engine with Prisma, your AI game-dev companion, built in. Sketch a world, script it, model it, score it, then ship it to
              the browser or Windows and play it with friends by join code.
            </p>
            <div className="hero-ctas">
              {start}
              <DownloadButton installer={installer} variant="ghost" />
            </div>
            <ul className="hero-facts">
              <li>Runs in your browser</li>
              <li>8-player online, no port forwarding</li>
              <li>Free demo AI for every account</li>
            </ul>
          </div>
          <PrismArt />
        </div>
      </section>

      <section className="section editor-peek">
        <EditorMock />
      </section>

      <section className="section">
        <header className="section-head">
          <p className="eyebrow">Everything in one place</p>
          <h2>From the first block to the final boss.</h2>
          <p className="muted">
            Every tool a small team needs, in one engine, sharing one project. Nothing to wire together.
          </p>
        </header>
        <div className="feature-grid">
          {FEATURES.map((f) => (
            <article key={f.tag} className="feature" style={{ '--tone': f.color } as React.CSSProperties}>
              <span className="feature-tag">{f.tag}</span>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section steps-section">
        <header className="section-head">
          <p className="eyebrow">How it works</p>
          <h2>Press start.</h2>
        </header>
        <ol className="steps">
          {STEPS.map((s) => (
            <li key={s.n} className="step">
              <span className="step-n">{s.n}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section">
        <header className="section-head section-head-row">
          <div>
            <p className="eyebrow">The arcade</p>
            <h2>Made with Prism Engine Cloud.</h2>
          </div>
          <Link to="/games" className="btn btn-ghost">
            Browse all games →
          </Link>
        </header>
        {games === null ? (
          <div className="game-grid">
            {[0, 1, 2].map((i) => (
              <div key={i} className="game-card game-card-skeleton" />
            ))}
          </div>
        ) : games.length === 0 ? (
          <div className="empty-arcade">
            <span className="empty-arcade-icon" aria-hidden="true">
              ▶
            </span>
            <div>
              <strong>The arcade is waiting for its first cabinet.</strong>
              <p className="muted">Publish a game from the editor and it shows up here instantly.</p>
            </div>
          </div>
        ) : (
          <div className="game-grid">
            {games.map((g) => (
              <GameCard key={g.slug} game={g} />
            ))}
          </div>
        )}
      </section>

      <section className="section">
        <div className="cta-band">
          <div>
            <h2>Two ways to play the engine.</h2>
            <p className="muted">
              The browser editor needs nothing installed. The desktop app adds local AI models, your own CLIs, native Windows
              builds, and works with the same account.
            </p>
          </div>
          <div className="cta-band-actions">
            {start}
            <DownloadButton installer={installer} variant="ghost" />
          </div>
        </div>
      </section>
    </div>
  );
}

/** White light into a prism, a spectrum of game pieces out. */
function PrismArt() {
  return (
    <div className="prism-art" aria-hidden="true">
      <svg viewBox="0 0 520 420" className="prism-svg">
        <defs>
          <linearGradient id="prism-face" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.22" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0.04" />
          </linearGradient>
          <linearGradient id="beam-in" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0.95" />
          </linearGradient>
          {SPECTRUM.map((c, i) => (
            <linearGradient key={c} id={`ray-${i}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor={c} stopOpacity="0.95" />
              <stop offset="1" stopColor={c} stopOpacity="0" />
            </linearGradient>
          ))}
        </defs>
        <path d="M0 236 L200 214 L200 222 L0 246 Z" fill="url(#beam-in)" className="beam-in" />
        {SPECTRUM.map((c, i) => {
          const y = 70 + i * 46;
          return <path key={c} d={`M262 ${206 + i * 4} L520 ${y - 9} L520 ${y + 9} Z`} fill={`url(#ray-${i})`} className="ray" style={{ animationDelay: `${i * 0.18}s` }} />;
        })}
        <polygon points="232,96 330,300 134,300" fill="url(#prism-face)" stroke="rgba(255,255,255,0.55)" strokeWidth="2" className="prism-body" />
        <polygon points="232,96 252,300 134,300" fill="rgba(255,255,255,0.05)" />
      </svg>
      <div className="prism-pieces">
        {['🎮', '🗺️', '⚔️', '🎵', '🌲', '🚀', '👾'].map((icon, i) => (
          <span key={icon} className="piece" style={{ top: `${13 + i * 11}%`, borderColor: SPECTRUM[i], animationDelay: `${i * 0.25}s` }}>
            {icon}
          </span>
        ))}
      </div>
    </div>
  );
}

/** A still of the editor: scene tree, viewport, script and Prisma at work. */
function EditorMock() {
  return (
    <div className="mock-window">
      <div className="mock-chrome">
        <span className="mock-dot" style={{ background: '#ff5f57' }} />
        <span className="mock-dot" style={{ background: '#febc2e' }} />
        <span className="mock-dot" style={{ background: '#28c840' }} />
        <span className="mock-title">skyward — Prism Engine Cloud</span>
        <span className="mock-play">▶ Play</span>
      </div>
      <div className="mock-body">
        <aside className="mock-tree">
          <p className="mock-label">Scene</p>
          {['▾ World', '   ☀ Sun', '   ◉ Player', '   ▣ Camera', '   ▾ Islands', '      ◆ Isle A', '      ◆ Isle B', '   ✦ Coins ×24'].map((row, i) => (
            <div key={i} className={`mock-row${i === 2 ? ' is-selected' : ''}`}>
              {row}
            </div>
          ))}
        </aside>
        <div className="mock-viewport">
          <svg viewBox="0 0 600 340" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#1d2b64" />
                <stop offset="0.6" stopColor="#5b6fd6" />
                <stop offset="1" stopColor="#f7a072" />
              </linearGradient>
            </defs>
            <rect width="600" height="340" fill="url(#sky)" />
            <circle cx="470" cy="92" r="34" fill="#ffe29a" opacity="0.9" />
            <g opacity="0.25" stroke="#fff">
              {Array.from({ length: 12 }, (_, i) => (
                <line key={`v${i}`} x1={300 + (i - 6) * 20} y1="230" x2={300 + (i - 6) * 120} y2="340" />
              ))}
              {[240, 258, 284, 320].map((y) => (
                <line key={`h${y}`} x1="0" y1={y} x2="600" y2={y} />
              ))}
            </g>
            <polygon points="70,190 170,170 220,200 160,236 90,226" fill="#3a8f6a" />
            <polygon points="90,226 160,236 140,270 110,262" fill="#6b4f3a" />
            <polygon points="320,140 420,128 460,152 400,180 330,170" fill="#4cae7f" />
            <polygon points="330,170 400,180 380,214 350,204" fill="#7a5a40" />
            <circle cx="380" cy="118" r="6" fill="#ffd84d" />
            <circle cx="404" cy="112" r="6" fill="#ffd84d" />
            <circle cx="428" cy="118" r="6" fill="#ffd84d" />
            <rect x="140" y="146" width="16" height="24" rx="4" fill="#ff5d6c" />
            <rect x="136" y="140" width="24" height="34" rx="4" fill="none" stroke="#5b8cff" strokeWidth="2" strokeDasharray="4 3" />
            <path d="M148 176 L148 200 M148 176 L172 176" stroke="#f0605b" strokeWidth="2" />
          </svg>
          <span className="mock-fps">144 fps</span>
        </div>
        <aside className="mock-ai">
          <p className="mock-label">Prisma</p>
          <div className="mock-msg mock-msg-user">Add floating islands with coins between them</div>
          <div className="mock-msg">
            Placed 2 floating islands with a coin arc between them, and made the coins bob. Want a spring pad too? ✨
          </div>
          <div className="mock-tool">✓ scene.spawn ×26</div>
          <div className="mock-tool">✓ script.write coin.rhai</div>
        </aside>
      </div>
      <pre className="mock-code">
        <span className="c">{'// Coins bob gently; the player script collects them.'}</span>
        {'\n'}
        <span className="k">fn</span> <span className="f">on_update</span>(dt) {'{'}
        {'\n'}
        {'    state.t += dt * '}
        <span className="n">3.0</span>;{'\n'}
        {'    this.position = state.base + '}
        <span className="f">vec3</span>(<span className="n">0.0</span>, <span className="f">sin</span>(state.t) * <span className="n">0.08</span>, <span className="n">0.0</span>);{'\n'}
        {'}'}
      </pre>
    </div>
  );
}
